import type { ArchetypeDef, ParamRange } from "../schema/ability.js";

const r = (min: number, max: number, def: number): ParamRange => ({ min, max, default: def });

function arch(
  id: string,
  family: ArchetypeDef["family"],
  kind: ArchetypeDef["kind"],
  targets: ArchetypeDef["targets"],
  params: Record<string, ParamRange>,
  text: [string, string],
): ArchetypeDef {
  return { id, family, kind, targets, params, text: { de: text[0], en: text[1] } };
}

/** The parameterised ability library (30-units.md R23). Effects are implemented once in @zend/rules combat. */
export const ARCHETYPES: readonly ArchetypeDef[] = [
  // damage
  arch("abl.single_strike", "damage", "standard", "enemy", { mult: r(0.5, 1.5, 1.0) },
    ["Trifft ein Ziel für {mult}× Angriff.", "Hits one target for {mult}× attack."]),
  arch("abl.multi_strike", "damage", "active", "enemies", { targets: r(2, 4, 2), mult: r(0.5, 1.0, 0.7) },
    ["Trifft {targets} Ziele für je {mult}× Angriff.", "Hits {targets} targets for {mult}× attack each."]),
  arch("abl.aoe_blast", "damage", "active", "enemies", { mult: r(0.4, 0.9, 0.6) },
    ["Trifft alle Gegner für {mult}× Angriff.", "Hits every enemy for {mult}× attack."]),
  arch("abl.pierce", "damage", "active", "enemy", { pct: r(10, 60, 30), mult: r(0.9, 1.6, 1.2) },
    ["Ignoriert {pct}% Verteidigung, {mult}× Angriff.", "Ignores {pct}% defence, {mult}× attack."]),
  arch("abl.execute", "damage", "active", "enemy", { threshold: r(20, 40, 30), bonus: r(50, 150, 100) },
    ["+{bonus}% Schaden gegen Ziele unter {threshold}% Leben.", "+{bonus}% damage to targets below {threshold}% HP."]),
  arch("abl.chain", "damage", "active", "enemies", { bounces: r(2, 4, 2), decay: r(50, 90, 70), mult: r(0.8, 1.3, 1.0) },
    ["Springt auf {bounces} Ziele, jeder Sprung {decay}% des vorigen.", "Chains to {bounces} targets, each bounce {decay}% of the last."]),
  arch("abl.snipe_rear", "damage", "active", "rear", { mult: r(1.2, 2.2, 1.6) },
    ["Trifft Commander oder Support direkt für {mult}× Angriff.", "Strikes the commander or a support directly for {mult}× attack."]),
  arch("abl.barrage", "damage", "active", "enemy", { hits: r(3, 6, 3), mult: r(0.3, 0.6, 0.4) },
    ["{hits} Treffer für je {mult}× Angriff.", "{hits} hits for {mult}× attack each."]),
  arch("abl.overload", "damage", "ultimate", "enemy", { mult: r(2.0, 3.5, 2.5), selfPct: r(5, 15, 10) },
    ["{mult}× Angriff, kostet {selfPct}% eigenes Leben.", "{mult}× attack, costs {selfPct}% own HP."]),
  arch("abl.command_pulse", "commander", "standard", "enemy", { mult: r(0.6, 1.2, 0.8), rule: r(0, 9, 0) },
    ["Kommandoimpuls: {mult}× Angriff und Zielprotokoll.", "Command pulse: {mult}× attack and targeting protocol."]),
  arch("abl.explode_on_death", "damage", "passive", "enemies", { mult: r(1.0, 3.0, 1.5) },
    ["Explodiert beim Tod für {mult}× Angriff an allen Gegnern.", "Explodes on death for {mult}× attack to all enemies."]),
  arch("abl.summon", "damage", "active", "none", { count: r(1, 3, 2), every: r(40, 120, 60) },
    ["Ruft alle {every} Ticks {count} Shambler.", "Summons {count} Shamblers every {every} ticks."]),
  // debuff
  arch("abl.armor_break", "debuff", "active", "enemy", { pct: r(10, 40, 25), ticks: r(20, 60, 30) },
    ["−{pct}% Verteidigung für {ticks} Ticks.", "−{pct}% defence for {ticks} ticks."]),
  arch("abl.emp", "debuff", "active", "enemy", { drain: r(20, 60, 40) },
    ["Entzieht {drain} Energie.", "Drains {drain} energy."]),
  arch("abl.suppress", "debuff", "active", "enemy", { pct: r(10, 40, 25), ticks: r(20, 50, 30) },
    ["−{pct}% Angriffstempo für {ticks} Ticks.", "−{pct}% attack speed for {ticks} ticks."]),
  arch("abl.mark", "debuff", "active", "enemy", { pct: r(10, 30, 20), ticks: r(30, 60, 40) },
    ["Markiert: +{pct}% erlittener Schaden für {ticks} Ticks, zieht Feuer.", "Marks: +{pct}% damage taken for {ticks} ticks, draws fire."]),
  arch("abl.blind", "debuff", "active", "enemy", { pct: r(10, 40, 25), ticks: r(20, 50, 30) },
    ["−{pct}% Genauigkeit für {ticks} Ticks.", "−{pct}% accuracy for {ticks} ticks."]),
  arch("abl.corrode", "debuff", "active", "enemy", { pctPerSec: r(2, 6, 3), ticks: r(30, 80, 50) },
    ["{pctPerSec}% Angriff pro Sekunde als Säureschaden für {ticks} Ticks.", "{pctPerSec}% attack per second as acid damage for {ticks} ticks."]),
  arch("abl.shock", "debuff", "active", "enemy", { ticks: r(5, 15, 8), mult: r(0.8, 1.4, 1.0) },
    ["Betäubt {ticks} Ticks, {mult}× Angriff.", "Stuns for {ticks} ticks, {mult}× attack."]),
  // defence
  arch("abl.shield_self", "defence", "active", "self", { pct: r(15, 40, 25), ticks: r(30, 80, 50) },
    ["Schild über {pct}% max. Leben für {ticks} Ticks.", "Shield worth {pct}% max HP for {ticks} ticks."]),
  arch("abl.shield_ally", "defence", "active", "ally", { pct: r(10, 30, 20), ticks: r(30, 80, 50) },
    ["Schild über {pct}% max. Leben auf den schwächsten Verbündeten.", "Shield worth {pct}% max HP on the weakest ally."]),
  arch("abl.taunt", "defence", "active", "self", { ticks: r(20, 50, 30) },
    ["Zieht {ticks} Ticks lang alle Angriffe auf sich.", "Draws every attack for {ticks} ticks."]),
  arch("abl.fortify", "defence", "active", "self", { pct: r(15, 40, 25), ticks: r(30, 80, 50) },
    ["+{pct}% Verteidigung für {ticks} Ticks.", "+{pct}% defence for {ticks} ticks."]),
  arch("abl.reflect", "defence", "passive", "self", { pct: r(5, 25, 15) },
    ["Reflektiert {pct}% erlittenen Schaden.", "Reflects {pct}% of damage taken."]),
  arch("abl.bulwark", "defence", "passive", "self", { cap: r(10, 30, 20) },
    ["Kein Treffer verursacht mehr als {cap}% max. Leben.", "No single hit deals more than {cap}% max HP."]),
  // support
  arch("abl.repair_pulse", "support", "pulse", "ally", { pct: r(8, 20, 12) },
    ["Repariert den schwächsten Verbündeten um {pct}% max. Leben.", "Repairs the weakest ally for {pct}% max HP."]),
  arch("abl.repair_all", "support", "active", "allies", { pct: r(6, 15, 10) },
    ["Repariert alle Verbündeten um {pct}% max. Leben.", "Repairs every ally for {pct}% max HP."]),
  arch("abl.overcharge", "support", "active", "ally", { pct: r(10, 30, 20), ticks: r(30, 80, 50) },
    ["+{pct}% Angriff auf den stärksten Kämpfer für {ticks} Ticks.", "+{pct}% attack on the strongest fighter for {ticks} ticks."]),
  arch("abl.haste", "support", "active", "allies", { pct: r(10, 30, 20), ticks: r(30, 80, 50) },
    ["+{pct}% Angriffstempo für alle für {ticks} Ticks.", "+{pct}% attack speed for everyone for {ticks} ticks."]),
  arch("abl.energize", "support", "active", "allies", { amount: r(15, 40, 25) },
    ["+{amount} Energie für alle Verbündeten.", "+{amount} energy for every ally."]),
  arch("abl.cleanse", "support", "active", "allies", { count: r(1, 3, 2) },
    ["Entfernt {count} negative Effekte von jedem Verbündeten.", "Removes {count} negative effects from every ally."]),
  arch("abl.reboot", "support", "ultimate", "ally", { pct: r(30, 60, 40) },
    ["Reaktiviert einen zerstörten Kämpfer mit {pct}% Leben (einmal pro Kampf).", "Reboots one wrecked fighter at {pct}% HP (once per battle)."]),
  arch("abl.aura", "support", "aura", "allies", { stat: r(0, 4, 0), pct: r(3, 15, 6) },
    ["Aura: +{pct}% auf einen Wert für den ganzen Trupp.", "Aura: +{pct}% to one stat for the whole squad."]),
  // commander
  arch("abl.pulse_rally", "commander", "pulse", "allies", { pct: r(10, 25, 15), ticks: r(20, 40, 30) },
    ["Impuls: +{pct}% Angriff für {ticks} Ticks.", "Pulse: +{pct}% attack for {ticks} ticks."]),
  arch("abl.pulse_fortify", "commander", "pulse", "allies", { pct: r(10, 25, 15), ticks: r(20, 40, 30) },
    ["Impuls: +{pct}% Verteidigung für {ticks} Ticks.", "Pulse: +{pct}% defence for {ticks} ticks."]),
  arch("abl.pulse_focus", "commander", "pulse", "enemy", { pct: r(10, 25, 15), ticks: r(20, 40, 30) },
    ["Impuls: markiert das Protokollziel (+{pct}% Schaden) für {ticks} Ticks.", "Pulse: marks the protocol target (+{pct}% damage) for {ticks} ticks."]),
  arch("abl.pulse_overdrive", "commander", "pulse", "allies", { pct: r(10, 25, 15), ticks: r(20, 40, 30) },
    ["Impuls: +{pct}% Angriffstempo für {ticks} Ticks.", "Pulse: +{pct}% attack speed for {ticks} ticks."]),
  arch("abl.pulse_inspire", "commander", "pulse", "allies", { amount: r(10, 30, 20) },
    ["Impuls: +{amount} Energie für alle.", "Pulse: +{amount} energy for everyone."]),
  arch("abl.last_stand", "commander", "passive", "allies", { pct: r(10, 30, 20) },
    ["Fällt ein Kämpfer, werden alle anderen um {pct}% repariert.", "When a fighter falls, every other ally is repaired by {pct}%."]),
  arch("abl.protocol", "commander", "protocol", "none", { rule: r(0, 9, 0), bonusPct: r(0, 15, 10) },
    ["Zielprotokoll; +{bonusPct}% Schaden gegen das Protokollziel.", "Targeting protocol; +{bonusPct}% damage against the protocol target."]),
  arch("abl.ultimate_orbital", "commander", "ultimate", "enemies", { mult: r(1.5, 3.0, 2.0) },
    ["Orbitalschlag: {mult}× Angriff auf alle Gegner.", "Orbital strike: {mult}× attack on every enemy."]),
  arch("abl.ultimate_lockdown", "commander", "ultimate", "enemies", { ticks: r(10, 25, 15) },
    ["Lockdown: betäubt alle Gegner für {ticks} Ticks.", "Lockdown: stuns every enemy for {ticks} ticks."]),
  arch("abl.ultimate_reboot", "commander", "ultimate", "ally", { pct: r(30, 60, 40) },
    ["Reaktiviert einen zerstörten Kämpfer mit {pct}% Leben.", "Reboots one wrecked fighter at {pct}% HP."]),
];

const map = new Map(ARCHETYPES.map((a) => [a.id, a]));

export function archetypeById(id: string): ArchetypeDef {
  const a = map.get(id);
  if (!a) throw new Error(`unknown archetype ${id}`);
  return a;
}

/** Default localized display names per archetype, used when a unit does not name its ability itself. */
export const ARCHETYPE_NAMES: Record<string, [string, string]> = {
  "abl.single_strike": ["Schuss", "Shot"],
  "abl.multi_strike": ["Salve", "Salvo"],
  "abl.aoe_blast": ["Flächenschlag", "Blast"],
  "abl.pierce": ["Durchschlag", "Pierce"],
  "abl.execute": ["Exekution", "Execute"],
  "abl.chain": ["Kettenblitz", "Chain"],
  "abl.snipe_rear": ["Infiltration", "Infiltrate"],
  "abl.barrage": ["Sperrfeuer", "Barrage"],
  "abl.overload": ["Überlastung", "Overload"],
  "abl.command_pulse": ["Kommandoimpuls", "Command Pulse"],
  "abl.explode_on_death": ["Detonation", "Detonation"],
  "abl.summon": ["Schrei", "Scream"],
  "abl.armor_break": ["Panzerbrecher", "Armor Break"],
  "abl.emp": ["EMP", "EMP"],
  "abl.suppress": ["Unterdrückung", "Suppress"],
  "abl.mark": ["Markierung", "Mark"],
  "abl.blind": ["Blendung", "Blind"],
  "abl.corrode": ["Säure", "Corrode"],
  "abl.shock": ["Schock", "Shock"],
  "abl.shield_self": ["Schildwall", "Shield Wall"],
  "abl.shield_ally": ["Schutzschild", "Guard Shield"],
  "abl.taunt": ["Spott", "Taunt"],
  "abl.fortify": ["Verstärkung", "Fortify"],
  "abl.reflect": ["Reflexpanzer", "Reflect"],
  "abl.bulwark": ["Bollwerk", "Bulwark"],
  "abl.repair_pulse": ["Reparaturimpuls", "Repair Pulse"],
  "abl.repair_all": ["Vollreparatur", "Repair All"],
  "abl.overcharge": ["Überladung", "Overcharge"],
  "abl.haste": ["Beschleunigung", "Haste"],
  "abl.energize": ["Energieschub", "Energize"],
  "abl.cleanse": ["Reinigung", "Cleanse"],
  "abl.reboot": ["Neustart", "Reboot"],
  "abl.aura": ["Aura", "Aura"],
  "abl.pulse_rally": ["Sammelruf", "Rally"],
  "abl.pulse_fortify": ["Festungsbefehl", "Fortify Order"],
  "abl.pulse_focus": ["Feuer frei", "Focus Fire"],
  "abl.pulse_overdrive": ["Overdrive", "Overdrive"],
  "abl.pulse_inspire": ["Inspiration", "Inspire"],
  "abl.last_stand": ["Letztes Gefecht", "Last Stand"],
  "abl.protocol": ["Zielprotokoll", "Targeting Protocol"],
  "abl.ultimate_orbital": ["Orbitalschlag", "Orbital Strike"],
  "abl.ultimate_lockdown": ["Lockdown", "Lockdown"],
  "abl.ultimate_reboot": ["Systemneustart", "System Reboot"],
};
